import {
  ArrowUpRight,
  Boxes,
  ChevronRight,
  CircleAlert,
  Clock3,
  Image as ImageIcon,
  Map,
  MoveRight,
  PackageCheck,
  Play,
  RotateCcw,
  Sparkles,
  Upload,
  Warehouse,
} from "lucide-react";
import { useRef } from "react";
import type { DecisionPlan, LocationNode, WarehouseDataset } from "../types";
import type { PageId } from "../components/Shell";
import { WAREHOUSE_ZONE_COUNT } from "../lib/warehouse";

interface OverviewPageProps {
  dataset: WarehouseDataset;
  plan: DecisionPlan;
  locations: LocationNode[];
  mediaUrl: string;
  mediaType: "image" | "video";
  onNavigate: (page: PageId) => void;
  onImport: (file: File) => Promise<void>;
  onReplaceMedia: (file: File) => Promise<void>;
  onRestoreMedia: () => void;
}

export function OverviewPage({
  dataset,
  plan,
  locations,
  mediaUrl,
  mediaType,
  onNavigate,
  onImport,
  onReplaceMedia,
  onRestoreMedia,
}: OverviewPageProps) {
  const dataFileRef = useRef<HTMLInputElement>(null);
  const mediaFileRef = useRef<HTMLInputElement>(null);
  const dueSoon = plan.replenishments.filter((item) => item.stockDays <= item.cycle);
  const hotProducts = plan.products.filter((item) => item.category === "爆旺");
  const nearPlacements = plan.placements.filter((item) => item.category === "爆旺").slice(0, 3);
  const mainAisleCount = new Set(
    locations
      .filter((item) => item.encoded)
      .map((item) => `${item.zone}-${item.mainAisle}`),
  ).size;
  const smallAisleCount = new Set(
    locations
      .filter((item) => item.encoded)
      .map((item) => `${item.zone}-${item.mainAisle}-${item.smallAisle}`),
  ).size;

  return (
    <div className="overview-page">
      <section className="overview-hero">
        <div className="hero-copy">
          <div className="context-line">
            <span>{dataset.meta.asOf.replaceAll("-", ".")}</span>
            <i />
            <span>{dataset.meta.warehouse}</span>
            <i />
            <span>
              {dataset.meta.source === "api"
                ? "后端实时数据"
                : dataset.meta.source === "file"
                  ? "已导入数据"
                  : dataset.meta.source === "sample"
                    ? "旧版演示数据"
                    : "尚未接入数据"}
            </span>
          </div>
          <h2>让每一个货位<br /><em>为需求让路。</em></h2>
          <p>
            从出库趋势到拣货路径，从批次效期到补货周期。
            <br />仓序读取你的数据，给出今天可执行的布局与库存方案。
          </p>
          <div className="hero-actions">
            <button className="primary-button" onClick={() => onNavigate("assistant")}>
              {plan.products.length ? "查看决策方案" : "等待数据接入"} <ArrowUpRight size={17} />
            </button>
            <button className="secondary-button" onClick={() => onNavigate("warehouse")}>
              <Map size={17} /> 打开空间沙盘
            </button>
          </div>
          <div className="hero-evidence">
            <span><i className="evidence-mark" />{plan.quality.days} 天需求样本</span>
            <span><i className="evidence-mark" />{locations.length} 个实际库位</span>
            <span><i className="evidence-mark" />{plan.products.length} 个独立 SKU</span>
          </div>
        </div>

        <div className="decision-compass">
          <div className="compass-head">
            <span>DECISION COMPASS</span>
            <Sparkles size={16} />
          </div>
          <div className="compass-ring" aria-hidden="true">
            <div className="ring ring-one" />
            <div className="ring ring-two" />
            <div className="ring ring-three" />
            <span>{String(plan.hotCount).padStart(2, "0")}</span>
            <small>爆旺品项</small>
          </div>
          <div className="compass-title">{plan.headline}</div>
          <p>{plan.summary[1]}</p>
          <button className="text-link" onClick={() => onNavigate("assistant")}>
            阅读完整判断依据 <ChevronRight size={15} />
          </button>
        </div>
      </section>

      <section className={`evidence-band evidence-${plan.quality.level}`}>
        <div className="evidence-icon">
          <CircleAlert size={19} />
        </div>
        <div>
          <strong>{plan.quality.title}</strong>
          <p>{plan.quality.notes[0] ?? "字段完整，趋势、空间与补货建议可进入滚动复核。"}</p>
        </div>
        <span className="evidence-score">完整度 {(plan.quality.completeness * 100).toFixed(0)}%</span>
      </section>

      <section className="signal-grid" aria-label="关键经营指标">
        <div className="signal-primary">
          <span className="section-kicker">TODAY'S EXECUTION</span>
          <div className="signal-number">{dueSoon.length}</div>
          <h3>个品项进入补货窗口</h3>
          <p>按近 7 日热度、阻尼 Holt 预测区间与动态补货周期计算。</p>
          <button className="inline-action" onClick={() => onNavigate("replenishment")}>
            查看补货清单 <MoveRight size={16} />
          </button>
        </div>
        <div className="signal-list">
          <div>
            <span><Boxes size={17} />全库位</span>
            <strong>{locations.length}</strong>
            <small>{WAREHOUSE_ZONE_COUNT} 个真实库区 · {mainAisleCount} 条主通道 · {smallAisleCount} 条小通道</small>
          </div>
          <div>
            <span><Warehouse size={17} />近端建议</span>
            <strong>{plan.placements.filter((item) => item.category === "爆旺").length}</strong>
            <small>高频 SKU 需进入近端拣货带</small>
          </div>
          <div>
            <span><PackageCheck size={17} />效期风险</span>
            <strong>{dataset.batches.filter((item) => item.recallFlag).length}</strong>
            <small>召回标记批次将被自动拦截</small>
          </div>
        </div>
        <div className="velocity-card">
          <span className="section-kicker">VELOCITY MIX</span>
          <div className="velocity-bars">
            <div style={{ "--bar": `${(plan.hotCount / Math.max(1, plan.products.length)) * 100}%` } as React.CSSProperties}>
              <span>爆旺</span><strong>{plan.hotCount}</strong><i />
            </div>
            <div style={{ "--bar": `${(plan.stableCount / Math.max(1, plan.products.length)) * 100}%` } as React.CSSProperties}>
              <span>平销</span><strong>{plan.stableCount}</strong><i />
            </div>
            <div style={{ "--bar": `${(plan.slowCount / Math.max(1, plan.products.length)) * 100}%` } as React.CSSProperties}>
              <span>平滞</span><strong>{plan.slowCount}</strong><i />
            </div>
          </div>
          <button className="text-link" onClick={() => onNavigate("products")}>
            打开单品回归图 <ChevronRight size={15} />
          </button>
        </div>
      </section>

      <section className="cinema-section">
        <div className="cinema-media">
          {mediaType === "video" ? (
            <video src={mediaUrl} muted loop autoPlay playsInline aria-label="仓库空间影像" />
          ) : (
            <img src={mediaUrl} alt="仓库空间影像" />
          )}
          <div className="cinema-scrim" />
          <div className="cinema-topline">
            <span><Play size={12} /> CINEMATIC SPACE STUDY</span>
            <span>可替换影像 · {mediaType === "video" ? "VIDEO" : "STILL"}</span>
          </div>
          <div className="cinema-title">
            <span>WAREHOUSE FILM / 01</span>
            <h3>订单信号，<br />进入作业视野。</h3>
            <p>在你的仓库实景上叠加拣货距离、库位热度与补货节奏。</p>
          </div>
          <div className="media-actions">
            <button onClick={() => mediaFileRef.current?.click()}><Upload size={15} /> 替换影像</button>
            <button onClick={onRestoreMedia}><RotateCcw size={15} /> 恢复默认</button>
          </div>
        </div>
        <div className="cinema-rail">
          <div className="rail-head">
            <span>今日建议</span>
            <small>按执行优先级排序</small>
          </div>
          {nearPlacements.length ? nearPlacements.map((item, index) => (
            <button className="rail-row" key={item.sku} onClick={() => onNavigate("warehouse")}>
              <span className="rail-index">0{index + 1}</span>
              <span className="rail-copy">
                <strong>{item.product}</strong>
                <small>{item.to} · 距门口 {item.toDistance.toFixed(0)} 米</small>
              </span>
              <ArrowUpRight size={16} />
            </button>
          )) : (
            <div className="rail-empty">
              <ImageIcon size={20} />
              <strong>暂无近端搬迁建议</strong>
              <p>导入更多天的数据后可形成稳定趋势。</p>
            </div>
          )}
          <div className="rail-footer">
            <Clock3 size={16} />
            <span>方案生成于 {new Date(plan.generatedAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        </div>
      </section>

      <section className="quick-workflow">
        <div className="workflow-heading">
          <span className="section-kicker">FROM DATA TO ACTION</span>
          <h3>数据不需要被浏览，<br />它应该被翻译成动作。</h3>
        </div>
        <div className="workflow-steps">
          <button onClick={() => dataFileRef.current?.click()}>
            <span>01</span><strong>导入并校验</strong><small>CSV / JSON，自动识别中英文字段</small><ArrowUpRight size={16} />
          </button>
          <button onClick={() => onNavigate("products")}>
            <span>02</span><strong>解释需求</strong><small>近 3 / 7 日热度、Holt 预测与异常脉冲</small><ArrowUpRight size={16} />
          </button>
          <button onClick={() => onNavigate("warehouse")}>
            <span>03</span><strong>生成配位</strong><small>全库位沙盘与近远拣货带建议</small><ArrowUpRight size={16} />
          </button>
          <button onClick={() => onNavigate("assistant")}>
            <span>04</span><strong>输出方案</strong><small>布局、批次、补货与风险一并交付</small><ArrowUpRight size={16} />
          </button>
        </div>
      </section>

      <input
        ref={dataFileRef}
        className="sr-only"
        type="file"
        accept=".csv,.json,text/csv,application/json"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onImport(file);
          event.target.value = "";
        }}
      />
      <input
        ref={mediaFileRef}
        className="sr-only"
        type="file"
        accept="image/*,video/*"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onReplaceMedia(file);
          event.target.value = "";
        }}
      />
    </div>
  );
}
